import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  apiService,
  Auth0Config,
} from "../services/api";
import { pendoService } from "../services/pendo";
import appConfig from "../config";
import {
  clearClaimToken,
  getStoredDrawIds,
  getClaimToken,
  clearActiveDrawId,
  clearAllClaimTokens,
  clearWizardSession,
} from "../services/wizardStorage";

interface User {
  email?: string;
  name?: string;
  picture?: string;
  sub?: string;
  rootOrganisationName?: string;
  rootOrganisationId?: string;
  organisationId?: string;
  isAuthorisedPlexusUser?: boolean; // Super User flag
  isExternalUser?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  loginWithRedirect: () => void;
  logout: () => void;
  getAccessTokenSilently: () => Promise<string | null>;
  refreshAccessToken: () => Promise<string | null>;
  isSuperUser: boolean; // Convenient flag for Super User
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

// Helper function to extract tokens synchronously (runs before first render)
const getInitialTokens = (): {
  access_token: string;
  refresh_token: string;
} | null => {
  // Priority 1: Check URL hash first (Legal Gateway redirect)
  const hash = window.location.hash.substring(1);
  if (hash) {
    const params = new URLSearchParams(hash);
    const access_token = params.get("access_token");
    const refresh_token = params.get("refresh_token");

    if (access_token && refresh_token) {
      // Save to localStorage for persistence
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      // Clear hash from URL to prevent token leakage in browser history
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search
      );

      return { access_token, refresh_token };
    }
  }

  // Priority 2: Fallback to localStorage (returning user)
  const storedAccess = localStorage.getItem("access_token");
  const storedRefresh = localStorage.getItem("refresh_token");
  if (storedAccess && storedRefresh) {
    return { access_token: storedAccess, refresh_token: storedRefresh };
  }

  return null;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [config, setConfig] = useState<Auth0Config | null>(null);

  // Use ref to access config without causing callback recreation
  const configRef = React.useRef<Auth0Config | null>(null);
  configRef.current = config;

  // Initialize tokens in localStorage only (no React state needed)
  // This ensures getInitialTokens() runs only once during component initialization
  useState(() => getInitialTokens());

  // Load config from backend
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const authConfig = await apiService.getAuth0Config();
        setConfig(authConfig);
      } catch (err) {
        setError(err as Error);
        setIsLoading(false); // Stop loading on config error
      }
    };
    loadConfig();
  }, []);

  // Define clearTokens before it's used
  const clearTokens = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("isExternalUser");
    localStorage.removeItem("isRedirectingToLogin");
    clearActiveDrawId();
    clearAllClaimTokens();
    clearWizardSession();
    setUser(null);
  }, []);

  // Define refreshAccessToken before it's used
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    // Read from localStorage instead of state
    const refreshToken = localStorage.getItem("refresh_token");
    const config = configRef.current; // Read from ref to avoid dependency
    if (!refreshToken || !config) return null;
    try {
      // Use Legal Gateway's refresh endpoint
      const legalGatewayUrl = config.login_url.split("/gateway/login")[0];
      const response = await fetch(
        `${legalGatewayUrl}/gateway/api/random-draw/token/refresh`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${refreshToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const newAccessToken = data.access_token || data.accessToken;
        const newRefreshToken = data.refresh_token || data.refreshToken;

        if (!newAccessToken) {
          return null;
        }

        // Update localStorage only (no state updates to prevent re-renders)
        localStorage.setItem("access_token", newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem("refresh_token", newRefreshToken);
        }

        // Return the new access token immediately
        return newAccessToken;
      }

      return null;
    } catch (error) {
      return null;
    }
  }, []); // Empty deps - stable reference, never recreates, no re-renders

  const logout = useCallback(() => {
    clearTokens();

    // Clear Pendo session on logout
    pendoService.clearSession();

    if (config) {
      const legalGatewayUrl = config.login_url.split("/gateway/login")[0];
      window.location.href = `${legalGatewayUrl}/auth/logout/?from_app=random_draw`;
    } else {
      // Fallback if config not loaded
      window.location.href = "/";
    }
  }, [config, clearTokens]);

  const loginWithRedirect = useCallback(() => {
    if (!config) {
      return;
    }
    // Use the login URL provided by the backend
    // Backend constructs: {legal_gateway_url}/gateway/login?app=random_draw&next={frontend_url}
    window.location.href = config.login_url;
  }, [config]);

  // Fetch user info when access token is available
  useEffect(() => {
    const fetchUserInfo = async () => {
      // Read from localStorage instead of state
      const currentAccessToken = localStorage.getItem("access_token");

      if (!currentAccessToken || currentAccessToken === "null") {
        if (config) {
          const pathname = window.location.pathname;
          const isDrawsRoute = pathname.startsWith("/draws/");
          if (isDrawsRoute) {
            setIsLoading(false);
            return;
          }
          if (appConfig.features.anonymousDraftsEnabled) {
            window.location.href = "/draws/wizard";
            return;
          }
          // Config loaded, no token -> redirect to login
          // Keep isLoading=true so user sees loading spinner during redirect
          loginWithRedirect();
          return;
        }
        // Config not loaded yet, wait for it (keep loading)
        return;
      }

      // Need config to construct the Legal Gateway API URL
      // Without it, we can't fetch user info even with a valid token
      // Wait for config to load, then this effect will re-run
      if (!config) {
        return;
      }

      sessionStorage.removeItem("isRedirectingToLogin");

      try {
        setIsLoading(true);

        // Extract Legal Gateway base URL from login_url
        const legalGatewayUrl = config.login_url.split("/gateway/login")[0];

        // Call Legal Gateway's user-info endpoint (same as test.html)
        const response = await fetch(
          `${legalGatewayUrl}/gateway/api/random-draw/user-info`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${currentAccessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          // Map the Legal Gateway user format to our User interface
          const firstName = data.user.firstName || "";
          const lastName = data.user.lastName || "";
          const fullName =
            [firstName, lastName].filter(Boolean).join(" ").trim() ||
            data.user.email;

          const userInfo: User = {
            email: data.user.email,
            name: fullName,
            picture: data.user.picture, // If available
            sub: data.user.email, // Use email as sub
            rootOrganisationName: data.user.rootOrganisationName,
            rootOrganisationId: data.user.rootOrganisationId,
            organisationId: data.user.organisationId,
            isAuthorisedPlexusUser: data.user.isAuthorisedPlexusUser, // Super User flag
            isExternalUser: data.user.isExternalUser,
          };

          // Store isExternalUser in localStorage for use without useAuth dependency
          localStorage.setItem(
            "isExternalUser",
            String(data.user.isExternalUser)
          );

          setUser(userInfo);
          setError(null);
        } else if (response.status === 401) {
          // Token expired, try to refresh
          const newToken = await refreshAccessToken();
          if (newToken) {
            // Token refreshed successfully, fetch user info again with new token
            const retryResponse = await fetch(
              `${legalGatewayUrl}/gateway/api/random-draw/user-info`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${newToken}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (retryResponse.ok) {
              const data = await retryResponse.json();
              const firstName = data.user.firstName || "";
              const lastName = data.user.lastName || "";
              const fullName =
                [firstName, lastName].filter(Boolean).join(" ").trim() ||
                data.user.email;

              const userInfo: User = {
                email: data.user.email,
                name: fullName,
                picture: data.user.picture,
                sub: data.user.email,
                rootOrganisationName: data.user.rootOrganisationName,
                rootOrganisationId: data.user.rootOrganisationId,
                organisationId: data.user.organisationId,
                isAuthorisedPlexusUser: data.user.isAuthorisedPlexusUser,
                isExternalUser: data.user.isExternalUser,
              };

              // Store isExternalUser in localStorage for use without useAuth dependency
              localStorage.setItem(
                "isExternalUser",
                String(data.user.isExternalUser)
              );

              setUser(userInfo);
              setError(null);
            } else {
              // Retry with new token failed, redirect to logout
              logout();
              return;
            }
          } else {
            // Refresh token failed or invalid, redirect to logout
            logout();
            return;
          }
        } else {
          throw new Error("Failed to fetch user info");
        }
      } catch (err) {
        setError(err as Error);
        // On any authentication error, redirect to logout
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, [config, refreshAccessToken, clearTokens, logout, loginWithRedirect]);

  useEffect(() => {
    if (!user) return;
    if (!appConfig.features.anonymousDraftsEnabled) return;

    const sweep = async () => {
      const drawIds = getStoredDrawIds();
      if (drawIds.length === 0) return;
      for (const drawId of drawIds) {
        const token = getClaimToken(drawId);
        if (!token) continue;
        try {
          await apiService.claimDraw(drawId, token);
          clearClaimToken(drawId);
        } catch {
          // Best-effort background claim; leave the token for the wizard to
          // retry interactively on a genuine failure.
        }
      }
    };
    sweep();
  }, [user]);

  const getAccessTokenSilently = useCallback(async (): Promise<
    string | null
  > => {
    // Read directly from localStorage
    const token = localStorage.getItem("access_token");
    if (!token || token === "null" || token === "undefined") {
      return null;
    }
    return token;
  }, []); // No dependencies - stable reference

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    refreshAccessToken,
    isSuperUser: user?.isAuthorisedPlexusUser === true, // Convenient Super User flag
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
